/********************************************************************************
 * Copyright (c) 2020 Cedalo GmbH
 *
 * This program and the accompanying materials are made available under the
 * terms of the Eclipse Public License 2.0 which is available at
 * http://www.eclipse.org/legal/epl-2.0.
 *
 * SPDX-License-Identifier: EPL-2.0
 *
 ********************************************************************************/
import React, { Component } from 'react';
import { FormattedMessage } from 'react-intl';
import MenuList from '@mui/material/MenuList';
import SvgIcon from '@mui/material/SvgIcon';
import MenuItem from '@mui/material/MenuItem';
import Divider from '@mui/material/Divider';
import Paper from '@mui/material/Paper';
import DeleteIcon from '@mui/icons-material/esm/Delete';
import InsertCells from '@mui/icons-material/esm/FormatIndentIncrease';
import DeleteCells from '@mui/icons-material/esm/FormatIndentDecrease';
import HideIcon from '@mui/icons-material/esm/VerticalAlignCenter';
import ShowIcon from '@mui/icons-material/esm/FormatLineSpacing';
import LockIcon from '@mui/icons-material/esm/Lock';
import AddNoteIcon from '@mui/icons-material/esm/Comment';
import RemoveNoteIcon from '@mui/icons-material/esm/SpeakerNotesOff';
import LockOpenIcon from '@mui/icons-material/esm/LockOpen';
import JsonIcon from '@mui/icons-material/esm/DataObject';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import JSG from '@cedalo/jsg-ui';
import { IconCopy, IconCut, IconPaste } from '../../utils/Icons';
import { graphManager } from '../../../GraphManager';
import CellPayloadPopover from '../CellPayloadPopover';

const getSingleCell = (range) => {
	if (range.isSingleCell()) {
		const dataProvider = range.sheet.cells;
		return dataProvider.getRC(range.getX1(), range.getY1());
	}
	return undefined;
};
export default class SheetContextMenu extends Component {
	constructor(props) {
		super(props);
		this.state = {
			context: false,
			top: '0px',
			left: '0px',
			showCellPayload: false,
			showCellPayloadDisabled: true
		};
		this.sheetmenu = React.createRef();
	}

	componentDidMount() {
		document.addEventListener('click', this.handleClick);
		JSG.NotificationCenter.getInstance().register(
			this,
			JSG.SheetInteraction.SHEET_SHOW_CONTEXT_MENU_NOTIFICATION,
			'onContextMenu'
		);

		/* eslint-disable react/no-did-mount-set-state */
		this.setState({
			context: false
		});

		/* eslint-enable react/no-did-mount-set-state */
	}

	componentWillUnmount() {
		document.removeEventListener('click', this.handleClick);
		JSG.NotificationCenter.getInstance().unregister(
			this,
			JSG.SheetInteraction.SHEET_SHOW_CONTEXT_MENU_NOTIFICATION
		);
	}

	onContextMenu(data) {
		let left = data.object.event.event.offsetX;
		const maxRight = left + 235;
		if (maxRight > window.innerWidth) {
			left = window.innerWidth - 235;
		}
		const popHeight = 650;
		const maxTop = window.innerHeight - popHeight > 0 ? window.innerHeight - popHeight : 0;
		const top = data.object.event.event.offsetY > maxTop ? maxTop : data.object.event.event.offsetY;
		let payloadDisabled = true;
		let showHeaderMenus = false;
		let showCellPayloadDisabled = true;

		const sheetView = graphManager.getActiveSheetView();
		if (sheetView) {
			const selection = sheetView.getOwnSelection();
			if (selection.getSize() === 1) {
				const range = selection.getAt(0);
				const singleCell = getSingleCell(range);
				if (range.getWidth() === 2 && !range.isColumnRange()) {
					payloadDisabled = false;
				}
				showCellPayloadDisabled = !singleCell || singleCell.rawtype !== 'object';
			}
			if (!(selection.areRowsSelected() && selection.areColumnsSelected())) {
				showHeaderMenus = selection.areRowsSelected() || selection.areColumnsSelected();
			}
		}

		this.setState({
			showHeaderMenus,
			payloadDisabled,
			showCellPayloadDisabled,
			context: true,
			left: `${left}px`,
			top: `${top}px`
		});
	}

	onCopy = () => {
		const sheetView = graphManager.getActiveSheetView();
		if (sheetView) {
			sheetView.copyCells(false);
			graphManager.getGraphEditor().invalidate();
		}
	};

	onCopyText = () => {
		const sheetView = graphManager.getActiveSheetView();
		if (sheetView) {
			sheetView.copyText();
		}
	};

	onCut = () => {
		const sheetView = graphManager.getActiveSheetView();
		if (sheetView) {
			sheetView.copyCells(true);
		}
	};

	onPaste = () => {
		const sheetView = graphManager.getActiveSheetView();
		if (sheetView) {
			sheetView.notifyMessage({
				id: 'SheetMessage.paste'
			});
		}
	};

	onDeleteContent = () => {
		this.props.showDialog('DeleteCellContent');
	};

	onInsertCells = () => {
		const sheetView = graphManager.getActiveSheetView();

		if (sheetView) {
			sheetView.insertCells(graphManager.getGraphEditor().getGraphViewer());
		}
	};

	onHideHeader = () => {
		const sheetView = graphManager.getActiveSheetView();

		if (sheetView) {
			const viewer = graphManager.getGraphEditor().getGraphViewer();
			const selection = sheetView.getOwnSelection();
			if (selection.areRowsSelected()) {
				viewer
					.getInteractionHandler()
					.execute(
						JSG.SheetCommandFactory.create(
							'command.SetHeaderSectionSizeCommand',
							sheetView.getItem().getRows(),
							-1,
							selection.getRanges(),
							0,
							false
						)
					);
			} else if (selection.areColumnsSelected()) {
				viewer
					.getInteractionHandler()
					.execute(
						JSG.SheetCommandFactory.create(
							'command.SetHeaderSectionSizeCommand',
							sheetView.getItem().getColumns(),
							-1,
							selection.getRanges(),
							0,
							false
						)
					);
			}
		}
	};

	onShowHeader = () => {
		const sheetView = graphManager.getActiveSheetView();

		if (sheetView) {
			const viewer = graphManager.getGraphEditor().getGraphViewer();
			const selection = sheetView.getOwnSelection();
			if (selection.areRowsSelected()) {
				viewer
					.getInteractionHandler()
					.execute(
						JSG.SheetCommandFactory.create(
							'command.SetHeaderSectionSizeCommand',
							sheetView.getItem().getRows(),
							-1,
							selection.getRanges(),
							-1,
							true
						)
					);
			} else if (selection.areColumnsSelected()) {
				viewer
					.getInteractionHandler()
					.execute(
						JSG.SheetCommandFactory.create(
							'command.SetHeaderSectionSizeCommand',
							sheetView.getItem().getColumns(),
							-1,
							selection.getRanges(),
							-1,
							true
						)
					);
			}
		}
	};

	onPasteContent = () => {
		this.props.showDialog('InsertCellContent');
	};

	onDeleteCells = () => {
		const sheetView = graphManager.getActiveSheetView();

		if (sheetView) {
			sheetView.deleteCells(graphManager.getGraphEditor().getGraphViewer());
		}
	};

	showCellPayloadPopover = () => {
		this.setState({
			context: false,
			showCellPayload: true
		});
	};
	hideCellPayloadPopover = () => {
		this.setState({
			context: false,
			showCellPayload: false
		});
	};

	onSetPayloadRange = () => {
		const sheetView = graphManager.getActiveSheetView();

		if (sheetView) {
			sheetView.setPayloadRange(graphManager.getGraphEditor().getGraphViewer(), true);
			const selection = sheetView.getItem().getOwnSelection();
			const range = selection.getRanges()[selection.getActiveRangeIndex()];
			const rangeCopy = range.copy();
			rangeCopy.shiftToSheet();
		}
	};

	areCellsProtected = () => {
		const sheetView = graphManager.getActiveSheetView();
		if (sheetView) {
			const selection = sheetView.getItem().getOwnSelection();
			if (selection) {
				const cell = selection.activeCell;
				if (cell) {
					const format = sheetView.getItem().model.getCellProperties(cell.x, cell.y, true);
					return format.protected;
				}
			}
		}

		return true;
	};

	hasNote = () => {
		const sheetView = graphManager.getActiveSheetView();
		if (sheetView) {
			const selection = sheetView.getItem().getOwnSelection();
			if (selection) {
				const cell = selection.activeCell;
				if (cell) {
					const format = sheetView.getItem().model.getCellProperties(cell.x, cell.y, true);
					return format.note !== undefined;
				}
			}
		}

		return true;
	};

	onRemovePayloadRange = () => {
		const sheetView = graphManager.getActiveSheetView();

		if (sheetView) {
			sheetView.setPayloadRange(graphManager.getGraphEditor().getGraphViewer(), false);
		}
	};

	onProtectCells = () => {
		const locked = this.areCellsProtected();
		const attributesMap = new JSG.Dictionary();

		attributesMap.put('protected', !locked);

		const sheetView = graphManager.getActiveSheetView();
		if (sheetView) {
			const selection = sheetView.getItem().getOwnSelection();
			const cmd = JSG.SheetCommandFactory.create(
				'command.CellAttributesCommand',
				selection.getRanges(),
				attributesMap
			);
			graphManager.synchronizedExecute(cmd);
		}
	};

	onSetNote = (note) => {
		const attributesMap = new JSG.Dictionary();

		attributesMap.put('note', note);

		const sheetView = graphManager.getActiveSheetView();
		if (sheetView) {
			const selection = sheetView.getItem().getOwnSelection();
			const cmd = JSG.SheetCommandFactory.create(
				'command.CellAttributesCommand',
				selection.getRanges(),
				attributesMap
			);
			graphManager.synchronizedExecute(cmd);
			if (note !== null) {
				setTimeout(() => {
						sheetView.handleNote({x: selection.getRanges()[0]._x1, y: selection.getRanges()[0]._y1},
							graphManager.getGraphViewer(), 0)
					}
				, 200);
			}
		}
	};

	handleClick = (event) => {
		const { context } = this.state;
		const wasOutside = !(event.target.contains === this.sheetmenu.current);

		if (wasOutside && context) {
			this.setState({
				context: false
			});
		}
	};

	render() {
		if (!this.state.context && !this.state.showCellPayload) {
			return null;
		}
		const locked = this.areCellsProtected();
		const note = this.hasNote();

		return (
			<React.Fragment>
				<Paper
					id="sheetmenu"
					ref={this.sheetmenu}
					style={{
						display: 'inline-block',
						position: 'absolute',
						// to cover tour step
						zIndex: '200000',
						left: [this.state.left],
						top: [this.state.top],
						visibility: [this.state.context ? 'visible' : 'hidden']
					}}
				>
					<MenuList>
						<MenuItem onClick={this.onCut} dense>
							<ListItemIcon>
								<IconCut />
							</ListItemIcon>
							<ListItemText primary={<FormattedMessage id="ContextMenus.Cut" defaultMessage="Cut" />} />
						</MenuItem>
						<MenuItem onClick={this.onCopy} dense>
							<ListItemIcon>
								<IconCopy />
							</ListItemIcon>
							<ListItemText primary={<FormattedMessage id="ContextMenus.Copy" defaultMessage="Copy" />} />
						</MenuItem>
						<MenuItem onClick={this.onCopyText} dense>
							<ListItemIcon>
								<IconCopy />
							</ListItemIcon>
							<ListItemText
								primary={<FormattedMessage id="ContextMenus.CopyText" defaultMessage="Copy as Text" />}
							/>
						</MenuItem>
						<MenuItem
							onClick={this.onPaste}
							dense
							disabled={JSG.clipSheet === undefined && JSG.clipXML === undefined}
						>
							<ListItemIcon>
								<IconPaste />
							</ListItemIcon>
							<ListItemText
								primary={<FormattedMessage id="ContextMenus.Paste" defaultMessage="Paste" />}
							/>
						</MenuItem>
						<MenuItem onClick={this.onPasteContent} disabled={JSG.clipSheet === undefined} dense>
							<ListItemIcon>
								<IconPaste />
							</ListItemIcon>
							<ListItemText
								primary={<FormattedMessage id="ContextMenus.PasteContent" defaultMessage="Paste" />}
							/>
						</MenuItem>
						<MenuItem onClick={this.onDeleteContent} dense>
							<ListItemIcon>
								<DeleteIcon />
							</ListItemIcon>
							<ListItemText
								primary={<FormattedMessage id="ContextMenus.DeleteContent" defaultMessage="Delete" />}
							/>
						</MenuItem>
						<Divider />
						<MenuItem onClick={this.onInsertCells} dense>
							<ListItemIcon>
								<InsertCells />
							</ListItemIcon>
							<ListItemText
								primary={<FormattedMessage id="ContextMenus.InsertCells" defaultMessage="Insert" />}
							/>
						</MenuItem>
						<MenuItem onClick={this.onDeleteCells} dense>
							<ListItemIcon>
								<DeleteCells />
							</ListItemIcon>
							<ListItemText
								primary={
									<FormattedMessage id="ContextMenus.DeleteCells" defaultMessage="Delete Cells" />
								}
							/>
						</MenuItem>
						{this.state.showHeaderMenus
							? [
									<Divider />,
									<MenuItem onClick={this.onShowHeader} dense>
										<ListItemIcon>
											<ShowIcon />
										</ListItemIcon>
										<ListItemText
											primary={
												<FormattedMessage id="ContextMenus.ShowHeader" defaultMessage="Show" />
											}
										/>
									</MenuItem>,
									<MenuItem onClick={this.onHideHeader} dense>
										<ListItemIcon>
											<HideIcon />
										</ListItemIcon>
										<ListItemText
											primary={
												<FormattedMessage id="ContextMenus.HideHeader" defaultMessage="Hide" />
											}
										/>
									</MenuItem>
							  ]
							: null}
						<Divider />
						<MenuItem onClick={this.onSetPayloadRange} disabled={this.state.payloadDisabled} dense>
							<ListItemIcon>
								<SvgIcon>
									<path
										// eslint-disable-next-line max-len
										d="M11,2A2,2 0 0,1 13,4V20A2,2 0 0,1 11,22H2V2H11M4,10V14H11V10H4M4,16V20H11V16H4M4,4V8H11V4H4M15,11H18V8H20V11H23V13H20V16H18V13H15V11Z"
									/>
								</SvgIcon>
								{/* <PayloadRange /> */}
							</ListItemIcon>
							<ListItemText
								primary={
									<FormattedMessage
										id="ContextMenus.SetPayloadRange"
										defaultMessage="Define Payload Range"
									/>
								}
							/>
						</MenuItem>
						<MenuItem onClick={this.onRemovePayloadRange} dense>
							<ListItemIcon>
								<SvgIcon>
									<path
										// eslint-disable-next-line max-len
										d="M4,2H11A2,2 0 0,1 13,4V20A2,2 0 0,1 11,22H4A2,2 0 0,1 2,20V4A2,2 0 0,1 4,2M4,10V14H11V10H4M4,16V20H11V16H4M4,4V8H11V4H4M17.59,12L15,9.41L16.41,8L19,10.59L21.59,8L23,9.41L20.41,12L23,14.59L21.59,16L19,13.41L16.41,16L15,14.59L17.59,12Z"
									/>
								</SvgIcon>
							</ListItemIcon>
							<ListItemText
								primary={
									<FormattedMessage
										id="ContextMenus.RemovePayloadRange"
										defaultMessage="Remove Payload Range"
									/>
								}
							/>
						</MenuItem>
						<MenuItem
							disabled={this.state.showCellPayloadDisabled}
							onClick={this.showCellPayloadPopover}
							dense
						>
							<ListItemIcon>
								<JsonIcon />
							</ListItemIcon>
							<ListItemText
								primary={
									<FormattedMessage id="ContextMenus.ShowPayload" defaultMessage="Show Payload" />
								}
							/>
						</MenuItem>
						<Divider />
						<MenuItem onClick={this.onProtectCells} dense>
							<ListItemIcon>{locked ? <LockOpenIcon /> : <LockIcon />}</ListItemIcon>
							<ListItemText
								primary={
									locked ? (
										<FormattedMessage
											id="ContextMenus.UnprotectCells"
											defaultMessage="Unprotect Cells"
										/>
									) : (
										<FormattedMessage
											id="ContextMenus.ProtectCells"
											defaultMessage="Protect Cells"
										/>
									)
								}
							/>
						</MenuItem>
						{note ? null : (
							<MenuItem onClick={() => this.onSetNote('')} dense>
								<ListItemIcon>
									<AddNoteIcon />
								</ListItemIcon>
								<ListItemText
									primary={<FormattedMessage id="ContextMenus.AddNote" defaultMessage="Add Note" />}
								/>
							</MenuItem>
						)}
						{note ? (
							<MenuItem onClick={() => this.onSetNote(null)} dense>
								<ListItemIcon>
									<RemoveNoteIcon />
								</ListItemIcon>
								<ListItemText
									primary={<FormattedMessage id="ContextMenus.RemoveNote" defaultMessage="Remove Note" />}
								/>
							</MenuItem>
						) : null}
					</MenuList>
				</Paper>
				<CellPayloadPopover
					open={this.state.showCellPayload}
					onClose={this.hideCellPayloadPopover}
					anchor={this.sheetmenu.current}
				/>
			</React.Fragment>
		);
	}
}
